/**
 * FileUploader Component - Drag and drop file upload with progress tracking
 * Supports photos, documents, and other file types with multi-file upload
 */

import { useState, useRef, useCallback } from 'react';
import { Upload, File, X, CheckCircle, AlertCircle, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { uploadMultiple } from '@/services/attachment.service';
import type { ParentEntityType, AttachmentType, Attachment, UploadProgress } from '@/data/attachmentTypes';
import { isImageMimeType } from '@/data/attachmentTypes';

interface FileUploaderProps {
  parentType: ParentEntityType;
  parentId: string;
  attachmentType: AttachmentType;
  acceptedTypes?: string[];
  maxFiles?: number;
  maxSizeMB?: number;
  onUploadComplete: (attachments: Attachment[]) => void;
  onUploadError?: (error: string) => void;
  disabled?: boolean;
  className?: string;
}

export function FileUploader({
  parentType,
  parentId,
  attachmentType,
  acceptedTypes = ['image/*', 'application/pdf'],
  maxFiles = 10,
  maxSizeMB = 10,
  onUploadComplete,
  onUploadError,
  disabled = false,
  className,
}: FileUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const validateFiles = useCallback((files: FileList | File[]): File[] => {
    const validFiles: File[] = [];
    
    for (const file of Array.from(files)) {
      if (file.size > maxSizeBytes) {
        setError(`File "${file.name}" exceeds ${maxSizeMB}MB limit`);
        continue;
      }
      validFiles.push(file);
    }
    
    return validFiles.slice(0, maxFiles);
  }, [maxSizeBytes, maxFiles, maxSizeMB]);

  const handleUpload = useCallback(async (files: FileList | File[]) => {
    if (disabled || files.length === 0) return;

    const validFiles = validateFiles(files);
    if (validFiles.length === 0) return;

    setUploading(true);
    setError(null);
    setProgress(validFiles.map(f => ({ fileName: f.name, progress: 0, status: 'pending' })));

    try {
      // Get current user - fallback to 'System' if not available
      const currentUser = 'User'; // Could be passed as prop or retrieved from context
      
      // Upload files - progress tracking can be added later with individual file uploads
      const attachments = await uploadMultiple(
        validFiles,
        parentType,
        parentId,
        attachmentType,
        currentUser
      );

      // Mark all as complete
      setProgress(validFiles.map(f => ({ fileName: f.name, progress: 100, status: 'complete' })));
      
      onUploadComplete(attachments);
      
      // Clear progress after successful upload
      setTimeout(() => setProgress([]), 2000);
    } catch (err) {
      const errorMessage = (err as Error).message || 'Upload failed';
      setError(errorMessage);
      onUploadError?.(errorMessage);
    } finally {
      setUploading(false);
    }
  }, [parentType, parentId, attachmentType, validateFiles, disabled, onUploadComplete, onUploadError]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !uploading) {
      setIsDragging(true);
    }
  }, [disabled, uploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (!disabled && !uploading && e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  }, [disabled, uploading, handleUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(e.target.files);
      // Reset input to allow selecting same file again
      e.target.value = '';
    }
  }, [handleUpload]);

  const handleClick = useCallback(() => {
    if (!disabled && !uploading) {
      inputRef.current?.click();
    }
  }, [disabled, uploading]);

  const getFileIcon = (fileName: string, mimeType?: string) => {
    if (isImageMimeType(mimeType)) {
      return <Image className="h-4 w-4 text-primary" />;
    }
    return <File className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Drop zone */}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors",
          isDragging && "border-primary bg-primary/5",
          disabled || uploading ? "border-muted bg-muted/20 opacity-50 cursor-not-allowed" : "border-border hover:border-primary/40 hover:bg-primary/5"
        )}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={maxFiles > 1}
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          disabled={disabled || uploading}
          className="hidden"
        />
        
        <div className="flex flex-col items-center">
          {uploading ? (
            <>
              <Upload className="h-8 w-8 text-muted-foreground animate-pulse mb-2" />
              <p className="text-sm text-muted-foreground">Uploading...</p>
            </>
          ) : isDragging ? (
            <>
              <Upload className="h-8 w-8 text-primary mb-2" />
              <p className="text-sm text-primary">Drop files here</p>
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Drag & drop files here, or click to select
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Max {maxFiles} files • Up to {maxSizeMB}MB each
              </p>
            </>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-6 px-2"
            onClick={() => setError(null)}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Progress list */}
      {progress.length > 0 && (
        <div className="space-y-2 bg-muted/30 rounded-lg p-3">
          {progress.map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              {p.status === 'complete' ? (
                <CheckCircle className="h-4 w-4 text-success shrink-0" />
              ) : p.status === 'error' ? (
                <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
              ) : (
                <File className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <span className="flex-1 truncate min-w-0">{p.fileName}</span>
              {p.status === 'uploading' && (
                <Progress value={50} className="w-16 h-1.5" />
              )}
              {p.status === 'complete' && (
                <span className="text-xs text-success">Done</span>
              )}
              {p.status === 'error' && p.error && (
                <span className="text-xs text-destructive truncate max-w-[100px]">{p.error}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Simple single-file uploader for quick uploads
 */
interface SimpleUploaderProps {
  onFileSelect: (file: File) => void;
  accept?: string[];
  disabled?: boolean;
  label?: string;
}

export function SimpleUploader({
  onFileSelect,
  accept = ['image/*', 'application/pdf'],
  disabled = false,
  label = "Upload file",
}: SimpleUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      onFileSelect(e.target.files[0]);
      e.target.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept.join(',')}
        onChange={handleFileChange}
        disabled={disabled}
        className="hidden"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="h-4 w-4 mr-2" />
        {label}
      </Button>
    </div>
  );
}

export default FileUploader;